import { useState, useCallback, useMemo } from "react";

const MAX_SELECTION = 200;

/**
 * Custom hook for managing pilgrim selection with advanced logic
 * - Supports multi-page selection
 * - Enforces 200-item limit
 * - Handles "select all" with overflow protection
 * - Persists selection across pagination/filtering
 * - Allows updating contract_id for selected pilgrims
 */
export const useAccommodationSelection = () => {
  const [selectedPilgrims, setSelectedPilgrims] = useState([]);

  // Get count of selected pilgrims
  const selectedCount = useMemo(() => selectedPilgrims.length, [selectedPilgrims]);

  // Check if limit is reached
  const isLimitReached = useMemo(() => selectedCount >= MAX_SELECTION, [selectedCount]);

  // Get remaining slots
  const remainingSlots = useMemo(() => Math.max(0, MAX_SELECTION - selectedCount), [selectedCount]);

  /**
   * Check if a pilgrim is selected
   */
  const isPilgrimSelected = useCallback(
    (pilgrimUid) => {
      return selectedPilgrims.some((p) => p.id === pilgrimUid);
    },
    [selectedPilgrims]
  );

  /**
   * Handle single pilgrim selection/deselection
   */
  const handleSelectOne = useCallback(
    (pilgrim, checked) => {
      if (checked) {
        // Check if already selected
        if (isPilgrimSelected(pilgrim.id)) {
          return;
        }

        // Check if limit reached
        if (isLimitReached) {
          return;
        }

        // Add to selection
        setSelectedPilgrims((prev) => [...prev, pilgrim]);
      } else {
        // Remove from selection
        setSelectedPilgrims((prev) =>
          prev.filter((p) => p.id !== pilgrim.id)
        );
      }
    },
    [isPilgrimSelected, isLimitReached]
  );

  /**
   * Handle select all for current page items
   * Respects max limit and already accommodated pilgrims
   */
  const handleSelectAll = useCallback(
    (checked, currentPageItems) => {
      if (checked) {
        // Filter out already accommodated pilgrims
        const selectableItems = currentPageItems?.filter(
          (p) => !p.is_accommodated
        );

        // Filter out already selected pilgrims
        const newSelections = selectableItems.filter(
          (p) => !isPilgrimSelected(p.id)
        );

        // Calculate how many we can add
        const remaining = MAX_SELECTION - selectedCount;

        if (remaining <= 0) {
          return; // Already at limit
        }

        // Take only what we can add (respect the limit)
        const itemsToAdd = newSelections.slice(0, remaining);

        setSelectedPilgrims((prev) => [...prev, ...itemsToAdd]);
      } else {
        // Unselect all items from current page
        const currentPageUids = currentPageItems.map((p) => p.id);
        setSelectedPilgrims((prev) =>
          prev.filter((p) => !currentPageUids.includes(p.id))
        );
      }
    },
    [selectedCount, isPilgrimSelected]
  );

  /**
   * Check if all selectable items on current page are selected
   */
  const isPageFullySelected = useCallback(
    (currentPageItems) => {
      const selectableItems = currentPageItems.filter(
        (p) => !p.is_accommodated
      );

      if (selectableItems.length === 0) {
        return false;
      }

      return selectableItems.every((p) => isPilgrimSelected(p.id));
    },
    [isPilgrimSelected]
  );

  /**
   * Check if page is partially selected
   */
  const isPagePartiallySelected = useCallback(
    (currentPageItems) => {
      const selectableItems = currentPageItems.filter(
        (p) => !p.is_accommodated
      );

      if (selectableItems.length === 0) {
        return false;
      }

      const selectedOnPage = selectableItems.filter((p) =>
        isPilgrimSelected(p.id)
      );

      return (
        selectedOnPage.length > 0 &&
        selectedOnPage.length < selectableItems.length
      );
    },
    [isPilgrimSelected]
  );

  /**
   * Update contract_id for a specific selected pilgrim
   */
  const updateContractId = useCallback((pilgrimUid, newContractId) => {
    setSelectedPilgrims((prev) =>
      prev.map((p) =>
        p.id === pilgrimUid
          ? { ...p, contract_id: newContractId }
          : p
      )
    );
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedPilgrims([]);
  }, []);

  /**
   * Check if a specific pilgrim can be selected
   * (not accommodated and limit not reached)
   */
  const canSelectPilgrim = useCallback(
    (pilgrim) => {
      if (pilgrim.is_accommodated) {
        return false;
      }

      // If already selected, can always deselect
      if (isPilgrimSelected(pilgrim.id)) {
        return true;
      }

      // Check if limit reached
      return !isLimitReached;
    },
    [isPilgrimSelected, isLimitReached]
  );

  return {
    selectedPilgrims,
    selectedCount,
    isLimitReached,
    remainingSlots,
    isPilgrimSelected,
    handleSelectOne,
    handleSelectAll,
    isPageFullySelected,
    isPagePartiallySelected,
    updateContractId,
    clearSelection,
    canSelectPilgrim,
    MAX_SELECTION,
  };
};

